import { Check, Clock, Shield, Calendar, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PricingSection({ onOpenForm }) {
  const includes = [
    "Licença anual individual",
    "Atualizações durante o período contratado",
    "Ativação por chave",
    "Suporte básico",
    "Melhorias evolutivas da plataforma",
  ];

  return (
    <section
      id="pricing"
      className="relative py-16 md:py-32 bg-[#1C1F26]"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Pre-header Statement */}
        <div className="max-w-2xl mx-auto mb-8 px-4 md:px-0">
          <div className="border border-[#2A2F38] rounded-sm p-5 md:p-6 text-center">
            <p className="text-[#9CA3AF] text-sm md:text-base mb-2">
              Empresas experientes conseguem reagir quando a operação muda.
            </p>
            <p className="text-[#E5E7EB] text-sm md:text-base font-medium">
              Empresas estruturadas conseguem perceber essa mudança antes.
            </p>
          </div>
        </div>

        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 text-xs font-mono tracking-widest uppercase text-[#F59E0B] bg-[#0E3A46] border border-[#F59E0B]/20 rounded-sm mb-6">
            Investimento
          </span>

          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#E5E7EB]">
            Modelo de <span className="text-[#F59E0B]">Licença</span>
          </h2>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {/* Trial Card */}
          <div className="bg-[#0F1115] border border-[#2A2F38] rounded-sm p-8" data-testid="trial-card">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 bg-[#10B981] rounded-full" />
              <span className="text-sm font-mono tracking-wider uppercase text-[#10B981]">
                Comece Agora
              </span>
            </div>

            <h3 className="font-heading text-3xl font-bold text-[#E5E7EB] mb-2">
              30 Dias Grátis
            </h3>
            <p className="text-[#9CA3AF] mb-6">
              Teste completo sem compromisso.
            </p>

            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                <Check className="h-4 w-4 text-[#10B981]" />
                Acesso a todas as funcionalidades
              </li>
              <li className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                <Check className="h-4 w-4 text-[#10B981]" />
                Sem necessidade de cartão
              </li>
              <li className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                <Check className="h-4 w-4 text-[#10B981]" />
                Cancele quando quiser
              </li>
            </ul>

            <Button
              onClick={onOpenForm}
              className="w-full bg-[#10B981] text-white font-bold uppercase tracking-wide hover:bg-[#059669] transition-all rounded-sm py-4"
              data-testid="trial-cta-button"
            >
              Iniciar Teste Gratuito
            </Button>
          </div>

          {/* Official License Card - CENTER (Main Product) */}
          <div className="relative bg-[#0F1115] border-2 border-[#F59E0B] rounded-sm p-8 shadow-glow" data-testid="official-card">
            {/* Badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="inline-flex items-center gap-1 px-4 py-1 text-xs font-bold tracking-wider uppercase bg-[#F59E0B] text-black rounded-sm">
                <Shield className="h-3 w-3" />
                Licença Oficial
              </span>
            </div>

            <div className="flex items-center gap-2 mb-4 mt-2">
              <Shield className="h-4 w-4 text-[#F59E0B]" />
              <span className="text-sm font-mono tracking-wider uppercase text-[#F59E0B]">
                Licença Anual
              </span>
            </div>

            {/* Destaque para parcelamento */}
            <div className="bg-[#1C1F26] border border-[#F59E0B]/30 rounded-sm p-4 mb-4">
              <p className="text-sm text-[#9CA3AF] mb-1">Parcelado em até</p>
              <h3 className="font-heading text-4xl font-bold text-[#F59E0B]">
                12x R$ 499,99
              </h3>
              <p className="text-xs text-[#6B7280] mt-1">no cartão de crédito</p>
            </div>

            {/* Valor à vista secundário */}
            <div className="text-center mb-6 pb-4 border-b border-[#2A2F38]">
              <p className="text-xs text-[#6B7280] mb-1">ou à vista</p>
              <p className="text-lg text-[#9CA3AF]">R$ 4.913,46</p>
            </div>

            <div className="pt-2 mb-4">
              <p className="text-sm font-mono tracking-wider uppercase text-[#6B7280] mb-3">
                Inclui:
              </p>
              <ul className="space-y-2">
                {includes.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-[#9CA3AF]">
                    <Check className="h-4 w-4 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="text-xs text-[#6B7280] space-y-1 pt-4 border-t border-[#2A2F38]">
              <p>Sem mensalidade recorrente automática.</p>
              <p>Sem fidelidade contratual forçada.</p>
              <p className="text-[#9CA3AF] mt-2 italic">A chave de licença é enviada por e-mail após a confirmação do pagamento.</p>
            </div>
          </div>

          {/* Benefit Card - Extension Incentive */}
          <div className="bg-[#0F1115] border border-[#2A2F38] rounded-sm p-8" data-testid="benefit-card">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-4 w-4 text-[#F59E0B]" />
              <span className="text-sm font-mono tracking-wider uppercase text-[#F59E0B]">
                Fidelização Antecipada
              </span>
            </div>

            <h3 className="font-heading text-2xl font-bold text-[#E5E7EB] mb-4">
              Extensão do <span className="text-[#F59E0B]">Período Licenciado</span>
            </h3>

            <p className="text-sm text-[#9CA3AF] mb-6">
              Ao converter durante o período de avaliação, sua licença terá <span className="text-[#E5E7EB] font-medium">13 meses</span> de validade em vez de 12.
            </p>

            {/* Visual Explanation */}
            <div className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-4 mb-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#9CA3AF]">Período de avaliação</span>
                  <span className="text-sm font-mono text-[#10B981]">30 dias</span>
                </div>
                <div className="flex items-center justify-center">
                  <span className="text-[#6B7280]">+</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#9CA3AF]">Licença ativada</span>
                  <span className="text-sm font-mono text-[#F59E0B]">13 meses</span>
                </div>
                <div className="border-t border-[#2A2F38] pt-3 mt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#E5E7EB] font-medium">Uso total aproximado</span>
                    <span className="text-lg font-mono font-bold text-[#F59E0B]">≈ 14 meses</span>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-xs text-[#6B7280] text-center">
              Benefício exclusivo para decisões tomadas durante o período de avaliação.
            </p>
          </div>
        </div>

        {/* Why Annual */}
        <div className="max-w-2xl mx-auto mt-16 text-center">
          <div className="bg-[#0F1115] border border-[#2A2F38] rounded-sm p-8">
            <Calendar className="h-8 w-8 text-[#F59E0B] mx-auto mb-4" />
            <h3 className="font-heading text-2xl font-bold text-[#E5E7EB] mb-4">
              Por que anual?
            </h3>
            <p className="text-[#9CA3AF] mb-2">
              Porque previsibilidade não se constrói em um mês.
            </p>
            <p className="text-[#E5E7EB]">
              O KpAction é uma estrutura. <span className="text-[#F59E0B]">Estruturas exigem continuidade.</span>
            </p>
          </div>
        </div>

        {/* Renewal/Acquisition Note */}
        <div className="max-w-xl mx-auto mt-12 text-center">
          <div className="border-t border-[#2A2F38] pt-8">
            <p className="text-sm font-mono tracking-wider uppercase text-[#6B7280] mb-3">
              Renovação e Aquisição de Licença
            </p>
            <p className="text-sm text-[#9CA3AF] mb-4">
              Para renovação de licença existente ou aquisição sem período de teste, acesse:
            </p>
            <a 
              href="/licenciamento-renovacoes" 
              className="inline-flex items-center gap-2 text-[#F59E0B] hover:text-[#FBBF24] transition-colors text-sm"
              data-testid="pricing-licenciamento-link"
            >
              Licenciamento e Renovações
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
